<template>
  <b-container class="main-container">
    <b-modal id="approve-modal" title="Update Status" @ok="updatePayRequestStatus">
      <p>Press 'OK' to mark this request with the selected status.</p>
      <p>You may also leave a note with this status change, which will be visible to the faculty member who made this pay request.</p>
      
      <b-form-group label="Status" label-class="control-label workload-course-label" :class="{ 'invalid-group': $v.form.statusId.$error }">
        <b-form-select :options="statuses" v-model="form.statusId" text-field="name" value-field="id" :class="{ 'form-control': true, 'is-invalid': $v.form.statusId.$error }"></b-form-select>
      </b-form-group>
      
      <b-form-group label="Notes" label-class="control-label workload-course-label">
        <b-form-textarea :rows="3" placeholder="(Optional) Enter notes..." v-model="form.statusNotes"></b-form-textarea>
      </b-form-group>
    </b-modal>

    <b-modal id="edit-modal" title="Edit Pay Request" v-model="openPayModal" @ok="updatePayRequest" @shown="editOpened">

      <!-- Sponsored - added Dec.  2021 -->
      <b-form-group v-if="summerPayRequest" label="Sponsored" label-class="control-label workload-course-label">
        <b-form-radio style="margin-top:18px; margin-bottom:15px;" name="sponsored"  :value="false" v-model="summerPayRequest.sponsored">Not sponsored</b-form-radio>
        <b-form-radio style="margin-top:18px; margin-bottom:15px;" name="sponsored" :value="true" v-model="summerPayRequest.sponsored">Sponsored</b-form-radio>
      </b-form-group>

      <b-form-group v-if="summerPayRequest" label="Semester" label-class="control-label workload-course-label">
        <b-form-select v-model="summerPayRequest.termId">
          <b-form-select-option v-for="term in this.terms" :key="term.termId" :value="term.termId">{{ term.description }}</b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-form-group label="PI" label-class="control-label workload-course-label" class="mb-2">
        <autocomplete :source="usersApi" :inputClass="{ 'form-control': true, 'is-invalid': $v.form.piAsuriteId.$error }"
                      resultsValue="piAsuriteId"
                      :resultsDisplay="userDisplay"
                      ref="userAutocomplete"
                      @selected="selectUser"
                      credentials="include"
                      :request-headers="requestHeaders"
                      ></autocomplete>
      </b-form-group>
    </b-modal>

    <h3 v-if="summerPayRequest">Summer Pay Request
      <!-- Marina: I kept 'isBuyoutManager' because it's pretty much the same privileges; can change if needed -->
      <b-button v-if="isSPManager" v-b-modal.edit-modal variant="primary" class="ml-2 pull-right">Edit</b-button>

      <!-- Only RAs can change the status (spManager) -->
      <b-button v-if="isSPManager" v-b-modal.approve-modal variant="primary" class="ml-2 pull-right">Change Status</b-button>
    </h3>

    <h4 v-if="summerPayRequest">{{ summerPayRequest.term.description }} - {{ summerPayRequest.piEmployee.firstName }} {{ summerPayRequest.piEmployee.lastName }}</h4>

    <b-row v-if="summerPayRequest">
      <b-col>
        <widget style="header-style:bold;" heading="Details">
          <b-row>
            <b-col>
              <dl>
                <dt>Semester</dt>
                <dd>{{ summerPayRequest.term.description }}</dd>

                <dt>PI</dt>
                <dd>
                  <router-link target="_blank" :to="{ name: 'facultyDetails', params: { asuriteId: summerPayRequest.piAsuriteId } }">
                    {{ summerPayRequest.piEmployee.firstName }} {{ summerPayRequest.piEmployee.lastName }}
                  </router-link>
                </dd>

                <dt>Date submitted</dt>
                <dd>{{ summerPayRequest.createdDate | formatDate}}</dd>
              </dl>
            </b-col>

            <b-col>
              <dl>
                <dt>Sponsored?</dt>
                <dd>{{ (summerPayRequest.sponsored) ? 'Yes' : 'No' }}</dd>

                <dt>Faculty</dt>
                <dd>
                  <router-link target="_blank" :to="{ name: 'facultyDetails', params: { asuriteId: summerPayRequest.employee.asuriteId } }">
                    {{ summerPayRequest.employee.firstName }} {{ summerPayRequest.employee.lastName }}
                  </router-link>
                </dd>

                <dt>Status</dt>
                <!-- Dec. 13, 2021: Add a sanity check and don't use statuses[0] -->
                <!-- <dd>{{ summerPayRequest.statuses[0].statusName }}</dd> -->
                <dd>{{ summerPayRequest.currentStatus ? summerPayRequest.currentStatus.statusName : "" }}</dd>
              </dl>
            </b-col>
          </b-row>
        </widget>
        
        <widget heading="History">
          <b-row>
            <b-col>
              <dl>
                <dt>History</dt>
                 <SummerPayHistory :statuses="summerPayRequest.statuses" />
               </dl>
            </b-col>
          </b-row>
        </widget>

        <widget heading="Supporting Documents">
          <!-- Must have the 'editable' attribute below to show the 'Add File' button -->
          <workload-files :editable="isSPManager || isSPSubmitter" :files="mappedFiles" @fileAdded="addFile"></workload-files>
        </widget>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapState } from "vuex";
import summerPayRequestApi from "../../api/summerPayRequests";
import Widget from "../../components/widget";
import WorkloadFiles from "../../components/workloads/workload-files";
import SummerPayHistory from "../../components/summer-pay/summer-pay-history";
import ToastrSettings from "../../settings/toastr-settings";
import toastr from "toastr";
import roleChecks from "../../misc/role-checks";
import { required } from "vuelidate/lib/validators";
import termsClass from "../../api/terms.js";
import Autocomplete from "../../components/autocomplete";
import summerPayRequests from '../../api/summerPayRequests';

// var htmlEncode = require("js-htmlencode").htmlEncode;
// var htmlDecode = require("js-htmlencode").htmlDecode;

export default {
  mixins: [roleChecks],
  name: "SummerPayDetails",

  data() {
    return {
      openPayModal: false,
      summerPayRequest: null,
      attemptSummerPayRequestSubmit: false,

      form: {
        statusId: null,
        statusNotes: null,
        asuriteId: 0,
        sponsored: false,
        term: null,
        termId: 0,
        piAsuriteId: 0      // added Dec. 2021
      },
      requestHeaders: { "X-Requested-With": "XMLHttpRequest" },
      statuses: []
    };
  },

  props: {
    id: {
      required: true
    }
  },

  components: {
    widget: Widget,
    SummerPayHistory,
    Autocomplete,
    WorkloadFiles
  },

  validations: {
    form: {
      statusId: { required },
      notes: {},
      asuriteId: 0,
      piAsuriteId: 0    // new Dec. 2021
    }
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      files: state => state.workloads.files
    }),
    mappedFiles() {
      return _.map(this.summerPayRequest.files, function(file) {
        return {
          fileId: file.fileId,
          name: file.fileName,
          createdBy: file.createdBy,
          createdDate: file.createdDate
        };
      });
    },
    usersApi: function() {
      return process.env.VUE_APP_BASE_URI + "/api/employees/search?search=";
    },
    searchApi: function() {
      return process.env.VUE_APP_BASE_URI + this.apiPath;
    },
    emptyText: function() {
      return "There are no " + this.objectName + "s selected.";
    },
    placeholder: function() {
      return "Search " + this.objectName + "s";
    }
  },

  methods: {
    load: function() {
      this.terms = Array();

      termsClass
        .getCurrentAndFutureTerms()
        .then(response => {
          _.filter(response.data, term => {
            if (term["termCode"].endsWith("4")) this.terms.push(term);
          });
        })
        .catch(function(error) {
          console.log("Error: ");
          console.log(error);
        });

      summerPayRequestApi
        .getById(this.id)
        .then(response => {
          this.summerPayRequest = response.data;
        })
        .catch(function(error) {
          console.log(error);
        });
    },
    editOpened() {
      this.$refs.userAutocomplete.display = this.summerPayRequest.piEmployee.fullName;
    },
    userDisplay: function(user) {
      return (
        user.firstName +
        " " +
        user.lastName +
        " (" +
        user.asuriteId +
        ") - " +
        (user.employeeJobs[0] ? user.employeeJobs[0].title + " - " + user.employeeJobs[0].department.description : "")

        // user.department +
        // " - " +
        // user.title
      );
    },
    selectUser: function(autoselectResponse) {
      this.summerPayRequest.piAsuriteId = autoselectResponse.selectedObject.asuriteId;
    },
    addFile(files) {
      var formData = new FormData();

      _.forEach(files, function(file) {
        // add each file to the form data
        formData.append("files[]", file);
      });

      summerPayRequestApi
        .postFiles(this.summerPayRequest.id, formData)
        .then(() => {
          toastr.options = ToastrSettings;
          toastr.success("The request has been saved.", "Success");
          this.load();
        });
    },
    removeFile(file) {
      this.$emit("fileRemoved", file);
    },

    updatePayRequestStatus() {
      this.$v.form.$touch();

      if (!this.$v.form.$error) {
        let updatedPayRequest = {
          summerPayRequestId: this.summerPayRequest.id,
          summerPayRequestStatusDefinitionId: this.form.statusId,
          notes: this.form.statusNotes
        };

        if (!updatedPayRequest.notes) updatedPayRequest.notes = "";

        summerPayRequestApi.postStatus(updatedPayRequest).then(() => {
          summerPayRequestApi.statusEmail(this.summerPayRequest.id);

          toastr.options = ToastrSettings;
          toastr.success("The request has been saved.", "Success");
          this.load();
        });
      }
    },
    updatePayRequest() {
      if (!this.$v.form.$error) {
        let updatedPayRequest = {
          id: this.summerPayRequest.id,
          termId: this.summerPayRequest.termId,
          asuriteId: this.summerPayRequest.asuriteId,
          piAsuriteId: this.summerPayRequest.piAsuriteId,     // new Dec. 2021
          sponsored: this.summerPayRequest.sponsored
        };

        summerPayRequestApi.putspRequest(updatedPayRequest).then(response => {
          toastr.options = ToastrSettings;
          toastr.success("The request has been saved.", "Success");
          this.load();
        });

        // summerPayRequestApi.statusEmail(this.user.id);
        // this.openPayModal = false;
      }
    }
  },
  created() {
    this.load();
    summerPayRequestApi.getStatusDefinitions().then(response => {
      this.statuses = response.data;
    });
  }
};
</script>

<style scoped>
.main-container {
  max-width: 100%;
  margin-top: 10px;
}
</style>
